import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External imports
import moment from 'moment';
// Internal imports
import SchoolStatus from '@linc-technologies/linced-common/enums/school-status';

export default class BehaviourDatesService extends Service {
  @service
  dates;

  @service
  terms;

  @service
  toaster;

  // Constants
  maxDaysSuspended = 15;

  maxCaregiverMeetingDate = 7;
  reviewDateDefaultDays = 7;
  dateFormat = 'YYYY-MM-DD';

  calculateSchoolDaysInRange(startDate, endDate, spanCalendarYears) {
    // transform dates to moments
    let dateToIncrement = moment(startDate, this.dateFormat).startOf('day');
    endDate = moment(endDate, this.dateFormat).startOf('day');

    // start date counts as first day
    let days = [startDate];
    while (dateToIncrement.isBefore(endDate)) {
      dateToIncrement.add(1, 'day');

      if (!spanCalendarYears) {
        // don't wrap calendar years
        let lastDayOfYear = this.terms.endOfCalendarYear;
        if (
          lastDayOfYear &&
          dateToIncrement.isSameOrAfter(moment(lastDayOfYear).startOf('day'))
        ) {
          return days;
        }
      }

      if (this.validateIsSchoolDay(dateToIncrement.format(this.dateFormat))) {
        days.push(dateToIncrement.format(this.dateFormat));
      }
    }

    return days;
  }

  getNextSchoolDay(day, direction) {
    let startDate = day.format(this.dateFormat);
    let year = day.format('YYYY');
    let heroDate;
    do {
      day.add(direction, 'days');

      // Just return the start date since there are no more valid school days.
      if (year !== day.format('YYYY')) {
        return startDate;
      }

      heroDate = this.dates.getDate(day.format(this.dateFormat));
    } while (isEmpty(heroDate) || heroDate.status === SchoolStatus.CLOSED);

    return day.format(this.dateFormat);
  }

  getLastValidSchoolDay(date, numDays) {
    let day = moment(date, this.dateFormat);
    let daysRemaining = numDays - 1; // Subtract a day because we count the start date as the first day.
    let lastValidSchoolDate = day.format(this.dateFormat);

    // Just bail early if num days is too low.
    if (numDays < 1) {
      return lastValidSchoolDate;
    }

    let year = day.format('YYYY');

    while (daysRemaining > 0) {
      day.add(1, 'days');

      if (day.format('YYYY') !== year) {
        // Don't wrap calendar years, this also serves as a fallback in case
        // there are no valid days, this will only ever loop until the new year.
        break;
      }

      // If school date status not closed and is truthy, valid school day
      if (this.validateIsSchoolDay(day.format(this.dateFormat))) {
        daysRemaining -= 1;
        lastValidSchoolDate = day.format(this.dateFormat);
      }
    }

    return lastValidSchoolDate;
  }

  getNumSchoolDaysBetweenDates(startDate, endDate) {
    // transform dates to moments
    let dateToIncrement = moment(startDate, this.dateFormat).startOf('day');
    endDate = moment(endDate, this.dateFormat).startOf('day');

    // start date counts as first day
    let numDays = 1;
    while (dateToIncrement.isBefore(endDate)) {
      dateToIncrement.add(1, 'day');

      // don't wrap calendar years
      let lastDayOfYear = this.terms.endOfCalendarYear;
      if (
        lastDayOfYear &&
        dateToIncrement.isSameOrAfter(moment(lastDayOfYear).startOf('day'))
      ) {
        return numDays;
      }

      // If school date status not closed and is truthy, valid school day
      if (this.validateIsSchoolDay(dateToIncrement.format(this.dateFormat))) {
        numDays++;
      }
    }

    return numDays;
  }

  validateDuration(startDate, endDate, maxDuration) {
    let maxEndDate = this.getLastValidSchoolDay(startDate, maxDuration);

    return !moment(endDate, this.dateFormat).isAfter(
      moment(maxEndDate, this.dateFormat),
      'day',
    );
  }

  validateDateOrder(startDate, endDate) {
    return !moment(startDate, this.dateFormat).isAfter(
      moment(endDate, this.dateFormat),
      'day',
    );
  }

  getDay(date) {
    if (isEmpty(date)) {
      return [undefined, false];
    }

    let day = this.dates.getDate(date);
    if (isEmpty(day)) {
      return [undefined, false];
    }

    return [day, true];
  }

  // validateIsSchoolDay returns true if the school is open on the given date
  validateIsSchoolDay(date) {
    let [day, ok] = this.getDay(date);
    if (!ok) {
      return false;
    }
    return day.status !== SchoolStatus.CLOSED && day.status;
  }

  // validateIsSchoolDayOrEmpty is used for validation error messages etc
  validateIsSchoolDayOrEmpty(date) {
    let [day, ok] = this.getDay(date);
    if (!ok) {
      return true;
    }

    return day.status !== SchoolStatus.CLOSED && day.status;
  }

  validateSpansCalendarYears(startDate, endDate) {
    return !moment(startDate, this.dateFormat).isSame(
      moment(endDate, this.dateFormat),
      'year',
    );
  }

  getSuspensionDateErrors(startDate, endDate, delApproval) {
    if (
      !delApproval &&
      !this.validateDuration(
        startDate,
        endDate,
        this.maxDaysSuspended,
        this.dateFormat,
      )
    ) {
      return `A suspensions max length is ${this.maxDaysSuspended} school days from the beginning of the expulsion`;
    }

    if (
      !this.validateDateOrder(
        startDate,
        endDate,
        this.maxDaysSuspended,
        this.dateFormat,
      )
    ) {
      return `Suspension start date must be before end date`;
    }
  }

  getExpulsionDateErrors(expulsion, prop, date) {
    if (isEmpty(date)) {
      return '';
    }
    let day = this.dates.getDate(date);
    if (isEmpty(day)) {
      return '';
    }

    if (!this.validateIsSchoolDayOrEmpty(date)) {
      return 'Must be a school day';
    }

    switch (prop) {
      case 'caregiverMeetingDate': {
        const suspensionEndDate = expulsion.get('suspensionEndDate');
        if (!isEmpty(suspensionEndDate)) {
          if (
            !this.validateDuration(
              suspensionEndDate,
              date,
              this.maxCaregiverMeetingDate,
              this.dateFormat,
            )
          ) {
            return `Caregiver meeting date must be ${this.maxCaregiverMeetingDate} days or less after suspension end date`;
          }
        }
        break;
      }
      case 'earlyReturnDate':
        if (date < expulsion.get('suspensionStartDate')) {
          return 'Must be later than suspension start date';
        }
        if (date >= expulsion.get('suspensionEndDate')) {
          return 'Must be earlier than suspension end date';
        }

        break;
      case 'suspensionEndDate':
      case 'noticeDate': {
        let startDate = expulsion.get(
          prop === 'suspensionEndDate' ? 'suspensionStartDate' : 'noticeDate',
        );
        if (!isEmpty(startDate)) {
          return this.getSuspensionDateErrors(
            startDate,
            date,
            expulsion.get('suspensionExtensionDelApproval'),
          );
        }
        break;
      }
    }
    return '';
  }
}
